import type { PlantContract, PlantStatus, SubscriptionStatus } from '@/shared/data-types';
import { UI_TEXT } from '@/shared/languages';
import { getRevenueDisplayType, isRevenueInfoComplete } from '../revenue-display';

export type PlantInfoChipKey =
  | 'preparing'
  | 'subscriptionEnded'
  | 'unsubscribed'
  | 'notConfigured'
  | 'complete';

export interface PlantInfoChipInfo {
  key: PlantInfoChipKey;
  label: string;
  /** 칩 배경+글자 색 클래스(heviton 시맨틱 토큰). */
  className: string;
}

interface PlantInfoChipInput {
  plantStatus?: PlantStatus;
  contract: PlantContract | null;
  subscription?: { status: SubscriptionStatus } | null;
}

const CHIP: Record<PlantInfoChipKey, { label: string; className: string }> = {
  preparing: { label: UI_TEXT.STATUS_PREPARING, className: 'bg-muted text-muted-foreground' },
  subscriptionEnded: {
    label: UI_TEXT.STATUS_SUBSCRIPTION_ENDED,
    className: 'bg-heviton-error/10 text-heviton-error',
  },
  unsubscribed: {
    label: UI_TEXT.STATUS_NOT_SUBSCRIBED,
    className: 'bg-heviton-error/10 text-heviton-error',
  },
  notConfigured: {
    label: UI_TEXT.PLANT_LIST_STATE_NOT_CONFIGURED,
    className: 'bg-heviton-warning/10 text-heviton-warning',
  },
  complete: {
    label: UI_TEXT.PLANT_LIST_STATE_COMPLETE,
    className: 'bg-heviton-info/10 text-heviton-info',
  },
};

/**
 * 발전소 정보 상태 칩 SSOT — 겹치는 상태(운영준비중·미구독·계약 미설정)를 **단일 칩**으로. 우선순위:
 * **운영준비중 > 구독종료·미구독 > 계약정보 미설정 > 설정완료** (못 돌아감 > 권한없음 > 정보미완 > 완료).
 * 구독 반영은 `includeSubscription` — 구독 게이트 롤아웃 전엔 데이터 미시딩으로 전부 '미구독' 오탐하므로 off
 * (게이트 플래그와 동반 활성). 정보목록 표시용 우선순위(운영준비중 최상위)는 스코프 selectability(구독>운영,
 * 게이팅용)와 목적이 달라 분리. 색은 heviton 시맨틱 토큰.
 */
export function plantInfoChip(
  input: PlantInfoChipInput,
  includeSubscription = false,
): PlantInfoChipInfo {
  const key = resolveChipKey(input, includeSubscription);
  return { key, ...CHIP[key] };
}

function resolveChipKey(input: PlantInfoChipInput, includeSubscription: boolean): PlantInfoChipKey {
  if (input.plantStatus === 'preparing') return 'preparing';
  if (includeSubscription) {
    const status = input.subscription?.status;
    if (status === 'expired' || status === 'canceled') return 'subscriptionEnded';
    if (status !== 'active') return 'unsubscribed';
  }
  if (!isRevenueInfoComplete(getRevenueDisplayType(input.contract))) return 'notConfigured';
  return 'complete';
}
